/**
 * Copyright (c) 2025 Celestis AI
 *
 * WARNING: Unauthorized copying, modification, distribution, or use of this
 * software, via any medium, is strictly prohibited without explicit written
 * permission from Celestis AI.
 *
 * All rights reserved.
 *
 * Utility functions for generating starfield visual effects.
 * Uses crypto.getRandomValues() instead of Math.random() for security compliance.
 */

/**
 * Get a cryptographically secure random number between 0 and 1.
 * Uses crypto.getRandomValues() as recommended by SonarQube.
 * 
 * @returns A random number between 0 and 1
 */
export function secureRandom(): number {
  const crypto = window.crypto || (window as any).msCrypto;
  if (!crypto?.getRandomValues) {
    // Modern browsers should always have crypto support
    // If not available, throw an error rather than using insecure Math.random()
    throw new Error('crypto.getRandomValues is not available in this environment');
  }
  
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  // Convert to 0-1 range
  return array[0] / (0xFFFFFFFF + 1);
}

/**
 * Generate star positions and animation properties for starfield effects.
 * Uses crypto.getRandomValues() instead of Math.random() for security compliance.
 * 
 * @param count - Number of stars to generate
 * @returns Array of star configuration objects
 */
export function generateStarfieldStars(count: number): Array<{
  left: string;
  top: string;
  animationDelay: string;
  animationDuration: string;
}> {
  const stars: Array<{
    left: string;
    top: string;
    animationDelay: string;
    animationDuration: string;
  }> = [];
  
  for (let i = 0; i < count; i++) {
    const left = secureRandom();
    const top = secureRandom();
    const delay = secureRandom();
    const duration = secureRandom();
    
    stars.push({
      left: `${left * 100}%`,
      top: `${top * 100}%`,
      animationDelay: `${delay * 3}s`,
      animationDuration: `${2 + duration * 3}s`,
    });
  }
  
  return stars;
}

